import { combineReducers } from 'redux';
import storage from 'redux-persist/lib/storage';
import { persistReducer, createTransform } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import { areasReducer } from '../store/appData/areas/reducer';
import { initialState as areasInitialState } from '../store/appData/areas/initialState';
import { authReducer } from '../store/userData/user/reducer';
import { initialState as authInitialState } from '../store/userData/user/initialState';
import { caresReducer } from '../store/userData/cares/reducer';
import { initialState as caresInitialState } from '../store/userData/cares/initialState';
import { tripsReducer } from '../store/userData/trips/reducer';
import { initialState as tripsInitialState } from '../store/userData/trips/initialState';
import { boundTimelineTripsForPersist } from '../store/userData/trips/timelineConstants';
import { carsReducer } from '../store/userData/cars/reducer';
import { initialState as carsInitialState } from '../store/userData/cars/initialState';
import { circlesReducer } from '../store/userData/circles/reducer';
import { initialState as circlesInitialState } from '../store/userData/circles/initialState';
import { profilesReducer } from '../store/userData/profiles/reducer';
import { initialState as profilesInitialState } from '../store/userData/profiles/initialState';
import { claimsReducer } from '../store/userData/claims/reducer';
import { initialState as claimsInitialState } from '../store/userData/claims/initialState';
import { filterReducer } from '../store/ui/filters/reducer';
import { initialState as filterInitialState } from '../store/ui/filters/initialState';
import { fleetsReducer } from '../store/appData/fleets/reducer';
import { initialState as fleetsInitialState } from '../store/appData/fleets/initialState';
import { pricingsReducer } from '../store/appData/pricings/reducer';
import { initialState as pricingsInitialState } from '../store/appData/pricings/initialState';
import { mapReducer } from '../store/ui/map/reducer';
import { initialState as mapInitialState } from '../store/ui/map/initialState';
import { uiReducer } from '../store/ui/common/reducer';
import { initialState as uiInitialState } from '../store/ui/common/initialState';
import { vehicleEventsReducer } from '../store/userData/vehicleEvents/reducer';
import { initialState as vehicleEventsInitialState } from '../store/userData/vehicleEvents/initialState';
import { categoriesReducer } from '../store/userData/categories/reducer';
import { initialState as categoriesInitialState } from '../store/userData/categories/initialState';
import { usersReducer } from '../store/userData/users/reducer';
import { initialState as usersInitialState } from '../store/userData/users/initialState';
import { adminsReducer } from '../store/userData/admins/reducer';
import { initialState as adminsInitialState } from '../store/userData/admins/initialState';
import { textsReducer } from '../store/appData/texts/reducer';
import { initialState as textsInitialState } from '../store/appData/texts/initialState';
import { receiptsReducer } from '../store/userData/receipts/reducer';
import { fuelTransactionsReducer } from '../store/userData/fuelTransactions/reducer';
import { initialState as receiptsInitialState } from '../store/userData/receipts/initialState';
import { initialState as fuelTransactionsInitialState } from '../store/userData/fuelTransactions/initialState';
import { reportsReducer } from '../store/userData/reports/reducer';
import { initialState as reportsInitialState } from '../store/userData/reports/initialState';
import { privacyPoliciesReducer } from '../store/appData/privacyPolicy/reducer';
import { initialState as privacyPoliciesInitialState } from '../store/appData/privacyPolicy/initialState';
import { sharepointReducer } from '../store/userData/sharepoint/reducer';
import { initialState as sharepointInitialState } from '../store/userData/sharepoint/initialState';

// Transform to strip modal states from ui.common before persisting
// and reset them to initial values when rehydrating
const uiCommonTransform = createTransform(
  // inbound: state being persisted (strip modal states)
  (inboundState: any) => {
    const {
      visibleModals,
      confirmModal,
      statusModal,
      selectedPayment,
      ...rest
    } = inboundState;
    return rest;
  },
  // outbound: state being rehydrated (reset modal states)
  (outboundState: any) => {
    const defaultDisplayColumns = uiInitialState.displayColumns;
    const persistedDisplayColumns = outboundState.displayColumns || {};

    // Merge persisted columns with defaults: keep ALL of the user's persisted
    // selections, then append any newly-introduced default columns. We must NOT
    // filter persisted columns down to the defaults — opt-in columns that aren't
    // in the defaults (e.g. the timeline's Status/Area/Fleet, or CarsTable's
    // battery/fuel) would otherwise be wiped on every reload. Stale keys for
    // removed columns are harmless: every consumer filters its DEFINED columns by
    // this set (getFilteredColumns / orderedExtraColumns), so unknown keys no-op.
    const mergedDisplayColumns = Object.keys(defaultDisplayColumns).reduce(
      (acc, key) => {
        const defaultCols: string[] = defaultDisplayColumns[key];
        const persistedCols: string[] = persistedDisplayColumns[key] || [];
        const newDefaults = defaultCols.filter(
          (col) => !persistedCols.includes(col)
        );
        acc[key] = [...persistedCols, ...newDefaults];
        return acc;
      },
      {} as typeof defaultDisplayColumns
    );

    return {
      ...outboundState,
      displayColumns: mergedDisplayColumns,
      visibleModals: [],
      confirmModal: null,
      statusModal: null,
      selectedPayment: null,
    };
  },
  { whitelist: ['common'] }
);

// List of slices and the fields to reset
const slicesUsersDataToReset: Record<string, string[]> = {
  cars: ['selected'],
  cares: ['selected'],
  claims: ['selected'],
  trips: ['selected', 'selectedBooking', 'updatingTrip'],
  categories: ['selected'],
  circles: ['selected'],
  profiles: ['selected'],
  users: ['selected'],
  admins: ['selected'],
  receipts: ['selected'],
  fuelTransactions: ['selected'],
  reports: ['selected'],
};

const slicesAppDataToReset: Record<string, string[]> = {
  areas: ['selected'],
  pricings: ['selected'],
  privacyPolicies: ['selected'],
};

const resetUserDataSelectedTransform = createTransform(
  // inbound: what is persisted
  (inboundState: any, key) => {
    // key = slice name inside userData
    const fieldsToReset = slicesUsersDataToReset[key] || [];
    const stateCopy = { ...inboundState };
    fieldsToReset.forEach((field) => {
      delete stateCopy[field]; // remove field from persistence
    });
    return stateCopy;
  },
  // outbound: what is rehydrated
  (outboundState: any, key) => {
    const fieldsToReset = slicesUsersDataToReset[key] || [];
    const initialStates: Record<string, any> = {
      cars: carsInitialState,
      cares: caresInitialState,
      claims: claimsInitialState,
      trips: tripsInitialState,
      categories: categoriesInitialState,
      circles: circlesInitialState,
      profiles: profilesInitialState,
      users: usersInitialState,
      admins: adminsInitialState,
      receipts: receiptsInitialState,
      fuelTransactions: fuelTransactionsInitialState,
      reports: reportsInitialState,
    };
    const initialSliceState = initialStates[key] || {};
    return {
      ...initialSliceState,
      ...outboundState, // restore persisted fields
      ...fieldsToReset.reduce((acc, field) => {
        acc[field] = initialSliceState[field]; // reset selected fields
        return acc;
      }, {} as any),
    };
  },
  { whitelist: Object.keys(slicesUsersDataToReset) } // only apply to these slices
);

const resetAppDataSelectedTransform = createTransform(
  // inbound: what is persisted
  (inboundState: any, key) => {
    // key = slice name inside userData
    const fieldsToReset = slicesAppDataToReset[key] || [];
    const stateCopy = { ...inboundState };
    fieldsToReset.forEach((field) => {
      delete stateCopy[field]; // remove field from persistence
    });
    return stateCopy;
  },
  // outbound: what is rehydrated
  (outboundState: any, key) => {
    const fieldsToReset = slicesAppDataToReset[key] || [];
    const initialStates: Record<string, any> = {
      areas: areasInitialState,
      pricings: pricingsInitialState,
      privacyPolicies: privacyPoliciesInitialState,
    };
    const initialSliceState = initialStates[key] || {};
    return {
      ...initialSliceState,
      ...outboundState, // restore persisted fields
      ...fieldsToReset.reduce((acc, field) => {
        acc[field] = initialSliceState[field]; // reset selected fields
        return acc;
      }, {} as any),
    };
  },
  { whitelist: Object.keys(slicesAppDataToReset) } // only apply to these slices
);

// Bound the persisted `trips.timelineTrips` to the last ~2 weeks, trimmed to the tile
// fields, so the (single, shared) userData localStorage blob can't blow the ~5MB quota —
// a full −3mo set of fat trip objects overflowed it and silently dropped the ENTIRE
// userData write. In-memory state keeps the full window; only the persisted copy shrinks.
const timelineTripsPersistTransform = createTransform(
  // inbound: what gets written to storage
  (inboundState: any, key) => {
    if (key !== 'trips' || !inboundState) return inboundState;
    return {
      ...inboundState,
      timelineTrips: boundTimelineTripsForPersist(inboundState.timelineTrips),
    };
  },
  // outbound: rehydrate the bounded subset as-is
  (outboundState: any) => outboundState,
  { whitelist: ['trips'] }
);

const userDataPersistConfig = {
  key: 'userData',
  storage,
  stateReconciler: autoMergeLevel2,
  transforms: [resetUserDataSelectedTransform, timelineTripsPersistTransform],
};

const uiPersistConfig = {
  key: 'ui',
  storage,
  stateReconciler: autoMergeLevel2,
  transforms: [uiCommonTransform],
};

const appDataPersistConfig = {
  key: 'appData',
  storage,
  stateReconciler: autoMergeLevel2,
  transforms: [resetAppDataSelectedTransform],
};

const rootReducer = combineReducers({
  userData: persistReducer(
    userDataPersistConfig,
    combineReducers({
      user: authReducer,
      cars: carsReducer,
      cares: caresReducer,
      claims: claimsReducer,
      trips: tripsReducer,
      vehicleEvents: vehicleEventsReducer,
      categories: categoriesReducer,
      circles: circlesReducer,
      profiles: profilesReducer,
      users: usersReducer,
      admins: adminsReducer,
      receipts: receiptsReducer,
      fuelTransactions: fuelTransactionsReducer,
      reports: reportsReducer,
      sharepoint: sharepointReducer,
    })
  ),
  ui: persistReducer(
    uiPersistConfig,
    combineReducers({
      common: uiReducer,
      filter: filterReducer,
      map: mapReducer,
    })
  ),
  appData: persistReducer(
    appDataPersistConfig,
    combineReducers({
      areas: areasReducer,
      fleets: fleetsReducer,
      pricings: pricingsReducer,
      privacyPolicies: privacyPoliciesReducer,
      texts: textsReducer,
    })
  ),
});

export const initialState = {
  userData: {
    user: authInitialState,
    cars: carsInitialState,
    cares: caresInitialState,
    claims: claimsInitialState,
    trips: tripsInitialState,
    vehicleEvents: vehicleEventsInitialState,
    categories: categoriesInitialState,
    circles: circlesInitialState,
    profiles: profilesInitialState,
    users: usersInitialState,
    admins: adminsInitialState,
    receipts: receiptsInitialState,
    fuelTransactions: fuelTransactionsInitialState,
    reports: reportsInitialState,
    sharepoint: sharepointInitialState,
  },
  ui: {
    common: uiInitialState,
    filter: filterInitialState,
    map: mapInitialState,
  },
  appData: {
    areas: areasInitialState,
    fleets: fleetsInitialState,
    pricings: pricingsInitialState,
    privacyPolicies: privacyPoliciesInitialState,
    texts: textsInitialState,
  },
};

export type RootState = typeof initialState;

export default rootReducer;
